<template>
  <el-dialog
    :visible.sync="visible"
    :title="`Monitriip - ${action} Viagem`"
    width="520px"
    custom-class="monitriip-driver-dialog"
    append-to-body
    :close-on-click-modal="false"
    @closed="onClosed"
  >
    <el-alert
      v-if="source === 'rfid'"
      type="success"
      :closable="false"
      title="Motorista identificado pela leitura RFID do veículo."
    />
    <el-alert
      v-else-if="source === 'associated'"
      type="info"
      :closable="false"
      title="Motorista associado ao veículo."
    />
    <el-alert
      v-else
      type="warning"
      :closable="false"
      title="Não foi possível identificar o motorista (falha na leitura RFID ou veículo offline). Selecione o motorista ou informe o CPF."
    />

    <el-radio-group v-model="mode" class="mode">
      <el-radio label="driver">Motorista registado</el-radio>
      <el-radio label="manual">Informar CPF manualmente</el-radio>
    </el-radio-group>

    <el-select
      v-if="mode === 'driver'"
      v-model="driverId"
      filterable
      placeholder="Selecionar motorista"
      style="width: 100%"
    >
      <el-option
        v-for="d in driverOptions"
        :key="d.id"
        :label="d.name"
        :value="d.id"
        :disabled="!d.cpf"
      >
        <span>{{ d.name }}</span>
        <span class="cpf">{{ d.cpf ? formatCpf(d.cpf) : 'sem CPF válido' }}</span>
      </el-option>
    </el-select>

    <template v-else>
      <el-input v-model="manualCpf" placeholder="000.000.000-00" maxlength="14" clearable />
      <div v-if="manualCpf && !manualCpfValid" class="error">CPF inválido.</div>
    </template>

    <div v-if="selection" class="summary">
      <div>Será enviado à Monitriip:</div>
      <div><strong>Motorista:</strong> {{ selection.name }}</div>
      <div><strong>CPF:</strong> {{ formatCpf(selection.cpf) }}</div>
    </div>

    <span slot="footer">
      <el-button size="small" type="info" @click="visible = false">{{ $t('settings.form_cancel') }}</el-button>
      <el-button size="small" type="warning" :disabled="!selection" @click="confirm">{{ action }} Viagem</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { cleanCpf, isValidCpf, formatCpf, driverCpf } from '@/utils/cpf'

// Resolves the driver/CPF sent with a Monitriip trip start/end. Tries the driver read by RFID
// (last position), then the driver associated with the vehicle, and otherwise lets the
// user pick a registered driver or type the CPF. The driver list comes from the server,
// so this works while the vehicle is offline.
export default {
  name: 'MonitriipDriverDialog',
  data() {
    return {
      visible: false,
      source: null,
      mode: 'driver',
      driverId: null,
      manualCpf: '',
      ending: false,
      resolve: null
    }
  },
  computed: {
    ...mapGetters(['drivers']),
    action() {
      return this.ending ? 'Terminar' : 'Iniciar'
    },
    driverOptions() {
      return this.drivers
        .map(d => ({ id: d.id, name: d.name, cpf: driverCpf(d) }))
        .sort((a, b) => (!a.cpf - !b.cpf) || a.name.localeCompare(b.name))
    },
    manualCpfValid() {
      return isValidCpf(this.manualCpf)
    },
    selection() {
      if (this.mode === 'manual') {
        return this.manualCpfValid ? { name: 'CPF informado manualmente', cpf: cleanCpf(this.manualCpf) } : null
      }
      const d = this.driverOptions.find(d => d.id === this.driverId)
      return d && d.cpf ? d : null
    }
  },
  methods: {
    formatCpf,
    // Resolves with { name, cpf } or null if cancelled.
    open(vehicle, ending = false) {
      this.ending = ending
      const driver = this.findDriver(vehicle)
      this.source = driver ? driver.source : null
      this.driverId = driver ? driver.id : null
      this.mode = 'driver'
      this.manualCpf = ''
      this.visible = true
      return new Promise(resolve => { this.resolve = resolve })
    },
    findDriver(vehicle) {
      const byUniqueId = uniqueId => uniqueId && uniqueId !== '0' &&
        this.drivers.find(d => d.uniqueId.trim() === String(uniqueId).trim() && driverCpf(d))
      const p = vehicle.position
      const rfid = p && !p.outdated && byUniqueId(p.attributes && p.attributes.driverUniqueId)
      if (rfid) return { id: rfid.id, source: 'rfid' }
      const associated = byUniqueId(vehicle.attributes.driverUniqueId)
      if (associated) return { id: associated.id, source: 'associated' }
      return null
    },
    confirm() {
      const selection = this.selection
      this.visible = false
      this.finish(selection)
    },
    onClosed() {
      this.finish(null)
    },
    finish(result) {
      if (this.resolve) {
        this.resolve(result)
        this.resolve = null
      }
    }
  }
}
</script>

<style>
.monitriip-driver-dialog.el-dialog {
  max-width: calc(100vw - 24px);
}
.monitriip-driver-dialog .mode {
  margin: 16px 0 12px;
}
.monitriip-driver-dialog .cpf {
  float: right;
  color: #8492a6;
  font-size: 12px;
  margin-left: 12px;
}
.monitriip-driver-dialog .error {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
}
.monitriip-driver-dialog .summary {
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: 4px;
  background: #fdf6ec;
  line-height: 1.7;
}
</style>
